import { Box } from '@mui/material';
import { useLayersRowReveal } from './useLayersRowReveal';
import { LayersRowName } from './LayersRowName';
import { LayersRowActions } from './LayersRowActions';
import type { LayersRowProps } from './layersTypes';

function resolveRowFillSx(selected: boolean, canHoverFill: boolean, revealed: boolean): Record<string, string> | Record<string, never> {
  if (selected) return { backgroundColor: 'var(--surface-primary-subtle-hover)' };
  if (canHoverFill && revealed) return { backgroundColor: 'var(--surface-neutral-subtle-hover)' };
  return {};
}

/**
 * A "layers tree" row: chevron · icon · name · hover-revealed move up/down · drag handle · eye ·
 * ⋮ menu · optional children. Generic and block-agnostic — see `layersTypes.ts`.
 *
 * Two contract props are accepted for `LayersRowProps` parity but not yet painted:
 *  - `scopeNote` / `iconTone` — surfaced as a native `title` + `data-icon-tone` on the icon slot
 *    (see `LayersRowName`); the full "custom to this block" badge treatment
 *    (`fieldRowPrimitives/CustomElementMark`) is Part 7's to wire once `FieldListEditor` rows
 *    render through this component.
 *  - `locked` hides any **destructive** ⋮ entry (interpreted as "delete"); it does not also hide
 *    `onMoveUp`/`onMoveDown` — the caller decides whether a locked row may still reorder.
 */
export function LayersRow({
  chevron,
  icon,
  name,
  accessibleName,
  hidden = false,
  onMoveUp,
  onMoveDown,
  dragHandle,
  eye,
  trailing,
  revealedTrailing,
  menuItems,
  menuDisabledReason,
  disabled = false,
  testId,
  children,
  nameContent,
  menuTestId,
  rootProps,
  selected = false,
  onSelect,
  scopeNote,
  locked = false,
  iconTone,
  fillWholeRow = false,
  hideChevronSlot = false,
  drillChevron,
  nameTestId,
  collapseIdleActions = false,
  nameSuffix,
  leading
}: LayersRowProps) {
  const { revealed, rowHandlers } = useLayersRowReveal();
  const { component: rootComponent = 'div', ref: rootRef, style: rootStyle, sx: rootSx, ...restRootProps } = rootProps ?? {};

  const canHoverFill = !disabled && !locked && Boolean(onSelect || dragHandle);
  const fillSx = resolveRowFillSx(selected, canHoverFill, revealed);

  const visibleMenuItems = locked ? menuItems?.filter((item) => !item.destructive) : menuItems;

  return (
    <Box
      component={rootComponent}
      ref={rootRef}
      style={rootStyle}
      data-testid={testId}
      data-selected={selected ? 'true' : undefined}
      {...rowHandlers}
      {...restRootProps}
      sx={{ width: '100%', minWidth: 0, maxWidth: '100%', ...(fillWholeRow ? fillSx : {}), ...rootSx }}
    >
      {leading}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--size-8)',
          height: 'var(--size-32)',
          minWidth: 0,
          width: '100%',
          maxWidth: '100%',
          ...(fillWholeRow ? {} : fillSx)
        }}
      >
        <LayersRowName
          chevron={chevron}
          hideChevronSlot={hideChevronSlot}
          icon={icon}
          name={name}
          accessibleName={accessibleName}
          nameContent={nameContent}
          hidden={hidden}
          onSelect={onSelect}
          selected={selected}
          scopeNote={scopeNote}
          iconTone={iconTone}
          nameTestId={nameTestId}
          nameSuffix={nameSuffix}
        />

        {trailing ? <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{trailing}</Box> : null}

        <LayersRowActions
          name={accessibleName ?? name}
          disabled={disabled}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          revealedTrailing={revealedTrailing}
          dragHandle={dragHandle}
          eye={eye}
          menuItems={visibleMenuItems}
          menuDisabledReason={menuDisabledReason}
          menuTestId={menuTestId}
          testId={testId}
          revealed={revealed}
          collapseIdle={collapseIdleActions}
        />

        {drillChevron ? (
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', pl: 'var(--size-8)', ml: 'var(--size-4)' }}>{drillChevron}</Box>
        ) : null}
      </Box>

      {children}
    </Box>
  );
}
